import type { VoidFunctionComponent } from 'react';

import type { UpgradeDistance } from '../../../../../shared/vulns/types';

import Loading from './Loading';
import RemediationSuggestion from './RemediationSuggestion';

type Props = {
  /**
   * Controls the display of a loading state in the component.
   * If true, the component will display a loading state.
   */
  isLoading?: boolean;
  /**
   * Specifies the current version of the package.
   */
  version: string;
  /**
   * Controls the display of a partial fix section in the component.
   * If true, the partial fix section will be displayed.
   */
  shouldShowPartialFix?: boolean;
  /**
   * Defines the version of the partial fix.
   * This value will be used if shouldShowPartialFix is true.
   */
  partialFix?: string;
  /**
   * Specifies the distance between the current version and the partial fix.
   * This value will be used if shouldShowPartialFix is true.
   */
  partialFixDistance?: UpgradeDistance;
  /**
   * Controls the display of a complete fix section in the component.
   * If true, the complete fix section will be displayed.
   */
  shouldShowCompleteFix?: boolean;
  /**
   * Defines the version of the complete fix.
   * This value will be used if shouldShowCompleteFix is true.
   */
  completeFix?: string;
  /**
   * Specifies the distance between the current version and the complete fix.
   * This value will be used if shouldShowCompleteFix is true.
   */
  completeFixDistance?: UpgradeDistance;
  /**
   * Controls the display of a 'no fix available' section in the component.
   * If true, the 'no fix available' section will be displayed.
   */
  shouldShowNoFix?: boolean;
  /**
   * Similar to no fix, but controls the display of an 'unable to calculate' section in the component.
   * This property should be used for dependencies where we cannot calculate a fix (e.g. user-defined dependencies).
   * If true, the 'unable to calculate' section will be displayed and the 'no fix' section will be hidden.
   */
  shouldShowUnableToCalculate?: boolean;
};

const RemediationPanels: VoidFunctionComponent<Props> = ({
  isLoading,
  version,
  shouldShowPartialFix,
  shouldShowNoFix,
  shouldShowUnableToCalculate,
  shouldShowCompleteFix,
  partialFix,
  partialFixDistance,
  completeFix,
  completeFixDistance,
}) => {
  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <RemediationSuggestion type="current" version={version} />
      {shouldShowPartialFix && (
        <RemediationSuggestion type="partial" version={partialFix} upgradeDistance={partialFixDistance} />
      )}
      {shouldShowCompleteFix && (
        <RemediationSuggestion
          type="complete"
          version={completeFix}
          upgradeDistance={completeFixDistance}
          useFixBadge
        />
      )}
      {shouldShowNoFix && !shouldShowUnableToCalculate && <RemediationSuggestion type="no-fix" />}
      {shouldShowUnableToCalculate && <RemediationSuggestion type="unable-to-calculate" />}
    </>
  );
};

export default RemediationPanels;
